(() => {
  'use strict';

  const STORAGE_KEY = 'anymaple:wishlist:v1';
  const MAX_ITEMS = 100;
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
  let renderVersion = 0;

  const normalizeHandles = (value) => {
    if (!Array.isArray(value)) return [];
    const unique = [];
    value.forEach((item) => {
      const handle = String(item || '').trim().toLowerCase();
      if (!HANDLE_PATTERN.test(handle) || unique.includes(handle)) return;
      unique.push(handle);
    });
    return unique.slice(0, MAX_ITEMS);
  };

  const readHandles = () => {
    try {
      return normalizeHandles(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]'));
    } catch (error) {
      return [];
    }
  };

  const writeHandles = (handles) => {
    const normalized = normalizeHandles(handles);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (error) {
      return readHandles();
    }
    return normalized;
  };

  const setButtonState = (button, saved) => {
    const title = button.dataset.productTitle || 'this product';
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      `${saved ? 'Remove' : 'Save'} ${title} ${saved ? 'from' : 'to'} wishlist`
    );
    const text = button.querySelector('.anymaple-wishlist-button__text');
    if (text) text.textContent = saved ? 'Saved' : 'Save';
  };

  const syncInterface = (root = document) => {
    const handles = readHandles();
    const saved = new Set(handles);
    root.querySelectorAll('[data-anymaple-wishlist-toggle]').forEach((button) => {
      setButtonState(button, saved.has(button.dataset.productHandle));
    });
    document.querySelectorAll('[data-anymaple-wishlist-link]').forEach((link) => {
      const count = handles.length;
      const badge = link.querySelector('.anymaple-wishlist-count');
      link.setAttribute('aria-label', `Wishlist, ${count} ${count === 1 ? 'item' : 'items'}`);
      if (badge) {
        badge.textContent = String(count);
        badge.hidden = count === 0;
      }
    });
    return handles;
  };

  const notifyChange = (handles) => {
    document.dispatchEvent(
      new CustomEvent('anymaple:wishlist:change', { detail: { handles: [...handles] } })
    );
  };

  const updateWishlist = (action, rawHandle = '') => {
    const handles = readHandles();
    if (action === 'clear') {
      const updated = writeHandles([]);
      syncInterface();
      notifyChange(updated);
      return updated;
    }

    const handle = String(rawHandle || '').trim().toLowerCase();
    if (!HANDLE_PATTERN.test(handle)) throw new Error('A valid product handle is required.');
    const index = handles.indexOf(handle);

    if (action === 'remove' || (action === 'toggle' && index >= 0)) {
      if (index < 0) return handles;
      handles.splice(index, 1);
    } else if (action === 'add' || action === 'toggle') {
      if (index >= 0) return handles;
      handles.unshift(handle);
    } else {
      throw new Error('Unsupported wishlist action.');
    }

    const updated = writeHandles(handles);
    syncInterface();
    notifyChange(updated);
    return updated;
  };

  const toggleHandle = (handle) => updateWishlist('toggle', handle);

  const onToggleClick = (event) => {
    const button = event.target.closest('[data-anymaple-wishlist-toggle]');
    if (!button) return;
    const handle = String(button.dataset.productHandle || '').trim().toLowerCase();
    if (!HANDLE_PATTERN.test(handle)) return;
    event.preventDefault();
    event.stopPropagation();
    toggleHandle(handle);
  };

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const appendHeart = (button) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute(
      'd',
      'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z'
    );
    svg.appendChild(path);
    button.appendChild(svg);
  };

  const safeProductImage = (source) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      if (url.hostname === 'cdn.shopify.com' || url.pathname.includes('/cdn/shop/')) {
        url.searchParams.set('width', '720');
      }
      return url.href;
    } catch (error) {
      return '';
    }
  };

  const createWishlistCard = (product, handle) => {
    const card = document.createElement('article');
    card.className = 'anymaple-wishlist-card';

    const productUrl = `/products/${encodeURIComponent(handle)}`;
    const media = document.createElement('div');
    media.className = 'anymaple-wishlist-card__media';
    const mediaLink = document.createElement('a');
    mediaLink.className = 'anymaple-wishlist-card__media-link';
    mediaLink.href = productUrl;

    const imageUrl = safeProductImage(product.featured_image);
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.width = 600;
      image.height = 750;
      mediaLink.appendChild(image);
    }

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'anymaple-wishlist-button anymaple-wishlist-button--card is-saved';
    remove.dataset.anymapleWishlistToggle = '';
    remove.dataset.productHandle = handle;
    remove.dataset.productTitle = product.title || 'this product';
    remove.setAttribute('aria-pressed', 'true');
    appendHeart(remove);
    setButtonState(remove, true);
    media.append(mediaLink, remove);

    const body = document.createElement('div');
    body.className = 'anymaple-wishlist-card__body';

    const title = document.createElement('a');
    title.className = 'anymaple-wishlist-card__title';
    title.href = productUrl;
    title.textContent = product.title || handle;

    const price = document.createElement('p');
    price.className = 'anymaple-wishlist-card__price';
    price.textContent = formatMoney(product.price);

    const view = document.createElement('a');
    view.className = 'anymaple-wishlist-card__view';
    view.href = productUrl;
    view.textContent = product.available === false ? 'View details' : 'Choose options';

    body.append(title, price, view);
    card.append(media, body);
    return card;
  };

  const fetchProduct = async (handle, signal) => {
    const requestOptions = {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    };
    if (signal) requestOptions.signal = signal;
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, requestOptions);
    if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
    return response.json();
  };

  const parseVerifiedFacts = (root) => {
    const node = root.querySelector('[data-anymaple-verified-facts]');
    if (!node) return null;

    try {
      const data = JSON.parse(node.textContent || '');
      const facts = Object.fromEntries(
        Object.entries(data.facts || {}).filter(([, value]) => String(value || '').trim())
      );
      return { ...data, facts };
    } catch (error) {
      console.warn('Unable to parse verified product facts', error);
      return null;
    }
  };

  const currentProductHandle = () => {
    const match = window.location.pathname.match(/^\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]).toLowerCase() : '';
  };

  const fetchVerifiedFacts = async (handle, signal) => {
    if (handle === currentProductHandle()) return parseVerifiedFacts(document);

    const requestOptions = {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
    };
    if (signal) requestOptions.signal = signal;
    const response = await fetch(`/products/${encodeURIComponent(handle)}`, requestOptions);
    if (!response.ok) throw new Error(`Product page request failed: ${response.status}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return parseVerifiedFacts(doc);
  };

  const registerWebMcpTools = () => {
    if (!document.modelContext || typeof document.modelContext.registerTool !== 'function') return;

    if (window.__anymapleWebMcpToolController) {
      window.__anymapleWebMcpToolController.abort();
    }
    const controller = new AbortController();
    window.__anymapleWebMcpToolController = controller;

    const register = (tool) => {
      document.modelContext
        .registerTool(tool, { signal: controller.signal })
        .catch((error) => console.warn(`Unable to register ${tool.name}`, error));
    };

    register({
      name: 'anymaple_get_warranty',
      title: 'Get Anymaple warranty information',
      description:
        'Returns Anymaple furniture warranty coverage, exclusions, claim requirements, and the official support page. Use this for warranty, damaged-item, missing-parts, or replacement-support questions.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true },
      execute: async () => ({
        warranty_name: '365-Day Limited Furniture Warranty',
        duration_days: 365,
        eligibility:
          'Eligible products purchased directly from anymaple.com by the original purchaser. Coverage starts on the documented delivery date.',
        coverage:
          'Verified manufacturing defects in materials or workmanship under normal residential use. Available remedies depend on the issue and inventory.',
        possible_remedies: [
          'Troubleshooting',
          'Replacement hardware or parts',
          'Replacement component',
          'Replacement product',
          'Another remedy required by applicable law',
        ],
        not_covered: [
          'Normal wear or cosmetic changes from ordinary use',
          'Improper assembly or misuse',
          'Commercial use',
          'Unauthorized modification or repair',
          'Exposure to moisture or extreme conditions',
          'Accidental damage after delivery',
          'Damage caused by moving or transporting assembled furniture',
        ],
        claim_requirements:
          'Email the order number, purchase email, product name, issue description, and clear photos or video. Include the assembly-manual part number when available and keep the product and packaging until the claim is resolved.',
        contact_email: 'Anymaple@hotmail.com',
        policy_url: `${window.location.origin}/pages/warranty-replacement`,
      }),
    });

    register({
      name: 'anymaple_get_verified_product_facts',
      title: 'Get verified Anymaple product facts',
      description:
        'Returns model-scoped, source-checked Anymaple product facts. You must call this after catalog search or get_product before answering model- or SKU-specific questions about dimensions, storage, drawers, charging, capacity, included items, or mixed configurations. If this conflicts with catalog copy, use the verified result. If verified is false, do not infer facts from a similar product.',
      inputSchema: {
        type: 'object',
        additionalProperties: false,
        properties: {
          handle: {
            type: 'string',
            pattern: '^[a-z0-9][a-z0-9-]*$',
            maxLength: 255,
            description:
              'Exact Shopify product handle. Omit only when already on the matching product page.',
          },
        },
      },
      annotations: { readOnlyHint: true },
      execute: async (input, options) => {
        const handle = String(input?.handle || currentProductHandle()).trim().toLowerCase();
        if (!HANDLE_PATTERN.test(handle)) throw new Error('A valid product handle is required.');
        const data = await fetchVerifiedFacts(handle, options && options.signal);
        const sourceUrl = `${window.location.origin}/products/${encodeURIComponent(handle)}`;

        if (!data) {
          return {
            verified: false,
            handle,
            source_url: sourceUrl,
            message:
              'No model-specific verified fact set is published for this product. Do not reuse facts from a similar product.',
          };
        }

        return {
          verified: true,
          ...data,
          source_url: sourceUrl,
          measurement_note: data.facts?.overall_size
            ? 'Measurements are approximate; allow clearance for moving parts, wall anchoring and assembly.'
            : null,
        };
      },
    });

    register({
      name: 'anymaple_get_wishlist',
      title: 'Get this browser wishlist',
      description:
        'Returns products saved to the Anymaple wishlist in this browser. The wishlist is local to this device and is not an account-synced list.',
      inputSchema: { type: 'object', properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: true },
      execute: async (_input, options) => {
        const handles = readHandles();
        const signal = options && options.signal;
        if (signal && signal.aborted) throw signal.reason;
        const results = await Promise.allSettled(
          handles.slice(0, 50).map((handle) => fetchProduct(handle, signal))
        );
        if (signal && signal.aborted) throw signal.reason;
        const items = [];
        results.forEach((result, index) => {
          if (result.status !== 'fulfilled') return;
          const product = result.value;
          items.push({
            handle: handles[index],
            title: product.title || handles[index],
            url: `${window.location.origin}/products/${encodeURIComponent(handles[index])}`,
            image: safeProductImage(product.featured_image),
            price: Number(product.price || 0) / 100,
            currency: window.Shopify?.currency?.active || 'USD',
            available: product.available !== false,
          });
        });
        return {
          scope: 'Current browser and device only; not synced to a customer account.',
          count: handles.length,
          items,
          omitted_count: Math.max(0, handles.length - items.length),
          wishlist_url: `${window.location.origin}/pages/wishlist`,
        };
      },
    });

    register({
      name: 'anymaple_update_wishlist',
      title: 'Update this browser wishlist',
      description:
        'Adds, removes, toggles, or clears products in the Anymaple wishlist stored in this browser. This changes only the current device and does not sync to a customer account.',
      inputSchema: {
        type: 'object',
        required: ['action'],
        additionalProperties: false,
        properties: {
          action: {
            type: 'string',
            enum: ['add', 'remove', 'toggle', 'clear'],
            description: 'Wishlist change to perform.',
          },
          handle: {
            type: 'string',
            pattern: '^[a-z0-9][a-z0-9-]*$',
            maxLength: 255,
            description: 'Shopify product handle. Required except when action is clear.',
          },
        },
      },
      annotations: { readOnlyHint: false, consequentialHint: false },
      execute: async (input, options) => {
        const action = String(input?.action || '');
        const handle = String(input?.handle || '').trim().toLowerCase();
        const before = readHandles();
        if (action !== 'clear' && !HANDLE_PATTERN.test(handle)) {
          throw new Error('A valid product handle is required.');
        }
        const willAdd = action === 'add' || (action === 'toggle' && !before.includes(handle));
        if (willAdd) await fetchProduct(handle, options && options.signal);
        const handles = updateWishlist(action, handle);
        return {
          changed: JSON.stringify(before) !== JSON.stringify(handles),
          action,
          handle: action === 'clear' ? null : handle,
          count: handles.length,
          handles,
          scope: 'Current browser and device only; not synced to a customer account.',
          wishlist_url: `${window.location.origin}/pages/wishlist`,
        };
      },
    });
  };

  const renderWishlistPage = async () => {
    const page = document.querySelector('[data-anymaple-wishlist-page]');
    if (!page) return;
    const currentVersion = ++renderVersion;
    const grid = page.querySelector('[data-anymaple-wishlist-grid]');
    const empty = page.querySelector('[data-anymaple-wishlist-empty]');
    const status = page.querySelector('[data-anymaple-wishlist-status]');
    const handles = readHandles();

    grid.replaceChildren();
    if (handles.length === 0) {
      empty.hidden = false;
      status.textContent = '';
      return;
    }

    empty.hidden = true;
    status.textContent = `Loading ${handles.length} saved ${handles.length === 1 ? 'item' : 'items'}…`;
    const results = await Promise.allSettled(handles.map(fetchProduct));
    if (currentVersion !== renderVersion) return;

    let loaded = 0;
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled') return;
      grid.appendChild(createWishlistCard(result.value, handles[index]));
      loaded += 1;
    });

    syncInterface(page);
    if (loaded > 0) {
      status.textContent = `${loaded} saved ${loaded === 1 ? 'item' : 'items'}`;
    } else {
      status.textContent = 'Your saved items could not be loaded. Please refresh and try again.';
    }
  };

  const initialize = () => {
    syncInterface();
    renderWishlistPage();
  };

  registerWebMcpTools();

  document.addEventListener('click', onToggleClick);
  document.addEventListener('anymaple:wishlist:change', renderWishlistPage);
  document.addEventListener('shopify:section:load', (event) => syncInterface(event.target));
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    syncInterface();
    renderWishlistPage();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
